import React, {forwardRef, useImperativeHandle, useReducer} from "react";
import PropTypes from "prop-types";

import './swipe_out_fade_in.css'
import useTimer from "../../hooks/useTimer";


const swipeThreshold = 10;
const horizontalSwipeRatio = 3;

const isHorizontalSwipe = (offsetX, offsetY) => {
    return (Math.abs(offsetX) > swipeThreshold && Math.abs(offsetX) / Math.abs(offsetY) > horizontalSwipeRatio)
}

/*
 * SwipeOut the children components and fade them in.
 * The onSwipeRight and on onSwipeLeft are executed after the swipe and before the fade.
 * It can be used animate a change of the children component after a swipe.
 *
 * It uses useImperativeHandle and forwardRef to allow the parent to
 * trigger the swipe (https://reactjs.org/docs/hooks-reference.html#useimperativehandle).
 * Even if this is an imperative code it is considered correct
 * to trigger imperative animation. (https://reactjs.org/docs/refs-and-the-dom.html)
 */
const SwipeOutFadeIn = forwardRef((props, ref) => {
    const {onSwipeRight, onSwipeLeft, mouseEnabled = false, children} = props;

    const [state, updateState] = useReducer(
        (oldState, newState) => ({...oldState, ...newState}),
        {
            beingTouch: false,
            horizontalTouch: false,
            beingSwipe: false,
            swipeDirection: null,
            moveTick: false,
            prevX: 0,
            prevY: 0,
            lastEventTime: 0,
            velocity: 0,
            style: {
                left: 0
            },
        }
    )

    // Timer use to throttle the move events (at most one every 30 ms)
    const [_, restartMoveTimer] = useTimer(() => updateState({moveTick: false}), 20); // eslint-disable-line no-unused-vars

    const handleStart = (x, y) => {
        if (!state.beingSwipe) {
            updateState({
                beingTouch: true,
                movementDetected: false,
                beingSwipe: false,
                swipeDirection: null,
                moveTick: false,
                prevX: x,
                prevY: y,
                lastEventTime: Date.now(),
                style: {
                    left: 0
                },
            })
        }
    }

    const handleMove = (x, y) => {
        if (!state.moveTick && state.beingTouch) {
            const offsetX = x - state.prevX;
            const offsetY = y - state.prevY;
            const currTime = Date.now();
            const elapsed = currTime - state.lastEventTime;

            let newState = {
                prevX: x,
                prevY: y,
                moveTick: true,
                lastEventTime: currTime,
                velocity: (offsetX * 1.0) / elapsed,
            }

            // Update if horizontal scroll: horizontal component minus vertical component > threshold
            if (isHorizontalSwipe(offsetX, offsetY) || state.movementDetected) {
                const left = state.style.left + offsetX;

                newState = {
                    ...newState,
                    movementDetected: true,
                    style: {...state.style, left: left},
                }
            }

            updateState(newState);
            restartMoveTimer();
        }
    }

    const swipeOutLeft = () => {
        updateState({
            beingTouch: false,
            movementDetected: false,
            beingSwipe: true,
            swipeDirection: 'left',
            style: {
                ...state.style,
                transform: `translateX(calc(-100% - ${state.style.left}px )`,
                transition: `transform 0.2s ease-out, opacity 0.2s`,
                opacity: 0
            }
        })
    }

    const swipeOutRight = () => {
        updateState({
            beingTouch: false,
            movementDetected: false,
            beingSwipe: true,
            swipeDirection: 'right',
            style: {
                ...state.style,
                transform: `translateX(calc(100% - ${state.style.left}px)`,
                transition: `transform 0.2s ease-out, opacity 0.2s`,
                opacity: 0,
            }
        })
    }

    const swipeBack = () => {
        updateState({
            beingTouch: false,
            movementDetected: false,
            beingSwipe: state.style.left > 0,
            style: {
                ...state.style,
                ...(state.style.left !== 0 ? {transform: `translateX(${-state.style.left}px)`} : {}),
                transition: "transform 0.2s ease-out"
            }
        })
    }

    const handleEnd = () => {
        if (state.velocity * state.style.left > 0 && state.velocity > 0.5) {
            swipeOutRight();
        } else if (state.velocity * state.style.left > 0 && state.velocity < -0.5) {
            swipeOutLeft();
        } else {
            swipeBack();
        }
    }

    const handleTransitionEnd = () => {
        if (state.beingSwipe) {
            if (state.swipeDirection === 'right') {
                onSwipeRight();
            } else if (state.swipeDirection === 'left') {
                onSwipeLeft();
            }

            // Fade in
            updateState({
                beingSwipe: false,
                style: {
                    left: 0,
                    transition: `opacity 0.1s ease-out 0.3s`,
                    opacity: 1
                }
            })
        }
    }

    const handleTouchStart = (touchStartEvent) => handleStart(
        touchStartEvent.targetTouches[0].clientX,
        touchStartEvent.targetTouches[0].clientY
    );

    const handleTouchMove = (touchMoveEvent) => handleMove(
        touchMoveEvent.targetTouches[0].clientX,
        touchMoveEvent.targetTouches[0].clientY
    );

    const handleTouchEnd = () => handleEnd();

    const handleMouseDown = (mouseDownEvent) => handleStart(mouseDownEvent.clientX, mouseDownEvent.clientY);

    const handleMouseMove = (mouseMoveEvent) => handleMove(mouseMoveEvent.clientX, mouseMoveEvent.clientY);

    const handleMouseUp = () => handleEnd();

    const handleMouseLeave = () => handleMouseUp();

    useImperativeHandle(ref, () => ({
        swipeOutRight: () => swipeOutRight(),
        swipeOutLeft: () => swipeOutLeft(),
    }));

    return <div className="swipeable">
        <div {...{
            className: "swipeable-item",
            style: state.style,
            onTouchStart: e => handleTouchStart(e),
            onTouchMove: e => handleTouchMove(e),
            onTouchEnd: () => handleTouchEnd(),
            onTransitionEnd: () => handleTransitionEnd(),
            ...(mouseEnabled ? {
                onMouseDown: e => handleMouseDown(e),
                onMouseMove: e => handleMouseMove(e),
                onMouseUp: () => handleMouseUp(),
                onMouseLeave: () => handleMouseLeave(),
            } : {})
        }}>
            {
                children
            }
        </div>
    </div>
})

SwipeOutFadeIn.propTypes = {
    onSwipeRight: PropTypes.func.isRequired,
    onSwipeLeft: PropTypes.func.isRequired,
    mouseEnabled: PropTypes.bool
}

export default SwipeOutFadeIn;